import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, APP_NAME } from "@/lib/api";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinGate } from "@/components/PinGate";
import { isParentUnlocked } from "@/lib/parentSession";
import { toast } from "sonner";

const Settings = () => {
    const navigate = useNavigate();
    const [behaviors, setBehaviors] = useState({ positive: [], negative: [] });
    const [newLabel, setNewLabel] = useState("");
    const [newCat, setNewCat] = useState("positive");
    const [newPts, setNewPts] = useState(10);
    const [unlocked, setUnlocked] = useState(isParentUnlocked());
    const [pinOpen, setPinOpen] = useState(!isParentUnlocked());

    const load = useCallback(async () => {
        try {
            const r = await api.get("/behaviors");
            setBehaviors(r.data);
        } catch {
            navigate("/login");
        }
    }, [navigate]);

    useEffect(() => {
        if (unlocked) load();
    }, [unlocked, load]);

    const addBehavior = async () => {
        if (!newLabel.trim()) return toast.error("Label required");
        try {
            await api.post("/behaviors", {
                label: newLabel.trim(),
                category: newCat,
                points: Number(newPts) || 5,
            });
            setNewLabel("");
            setNewPts(10);
            load();
            toast.success("Added");
        } catch {
            toast.error("Failed to add");
        }
    };

    const updateBehavior = async (b, label, points) => {
        try {
            await api.put(`/behaviors/${b.behavior_id}`, {
                label: label.trim(),
                category: b.category,
                points: Number(points) || 5,
            });
            toast.success("Saved");
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const removeBehavior = async (b) => {
        if (!window.confirm(`Delete "${b.label}"?`)) return;
        await api.delete(`/behaviors/${b.behavior_id}`);
        load();
    };

    return (
        <div className="min-h-screen bb-night-bg bb-stars">
            <PinGate
                open={pinOpen}
                onOpenChange={(o) => {
                    setPinOpen(o);
                    if (!o && !unlocked) navigate("/profile");
                }}
                onSuccess={() => {
                    setUnlocked(true);
                    setPinOpen(false);
                }}
                title="Behavior settings"
            />
            {!unlocked && null}
            {unlocked && (
                <>
            <header className="relative z-10 border-b border-[var(--bb-border)]">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <button onClick={() => navigate("/profile")} className="bb-btn-ghost flex items-center gap-2">
                        <ArrowLeft size={16} /> Parent Profile
                    </button>
                    <h1 className="font-display text-xl">Behavior catalog</h1>
                    <span className="text-xs text-[#6c7a9c]">{APP_NAME}</span>
                </div>
            </header>

            <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-10">
                <section className="bb-card p-6">
                    <h2 className="font-display text-2xl mb-4">Add new behavior</h2>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-6">
                            <Label>Label</Label>
                            <Input
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                placeholder="e.g. Made bed"
                                className="mt-1 bg-[#080b14] border-[#1f2942]"
                                data-testid="new-behavior-label"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <Label>Category</Label>
                            <select
                                value={newCat}
                                onChange={(e) => setNewCat(e.target.value)}
                                className="mt-1 w-full bg-[#080b14] border border-[#1f2942] rounded-md px-3 py-2"
                                data-testid="new-behavior-category"
                            >
                                <option value="positive">Positive</option>
                                <option value="negative">Negative</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <Label>Points</Label>
                            <Input
                                type="number"
                                value={newPts}
                                onChange={(e) => setNewPts(e.target.value)}
                                className="mt-1 bg-[#080b14] border-[#1f2942]"
                                data-testid="new-behavior-points"
                            />
                        </div>
                        <div className="md:col-span-1 flex items-end">
                            <button
                                onClick={addBehavior}
                                className="bb-btn-primary w-full !px-3"
                                data-testid="add-behavior-btn"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </div>
                </section>

                {[
                    { key: "positive", label: "Positive behaviors", color: "#ffd166" },
                    { key: "negative", label: "Negative behaviors", color: "#ff9f1c" },
                ].map(({ key, label, color }) => (
                    <section key={key}>
                        <h2 className="font-display text-2xl mb-4" style={{ color }}>
                            {label}
                        </h2>
                        <div className="space-y-2">
                            {behaviors[key].map((b) => (
                                <BehaviorRow
                                    key={b.behavior_id}
                                    b={b}
                                    onSave={updateBehavior}
                                    onDelete={removeBehavior}
                                />
                            ))}
                            {behaviors[key].length === 0 && (
                                <p className="text-[#6c7a9c] text-sm">None yet — add one above.</p>
                            )}
                        </div>
                    </section>
                ))}
            </main>
                </>
            )}
        </div>
    );
};

const BehaviorRow = ({ b, onSave, onDelete }) => {
    const [label, setLabel] = useState(b.label);
    const [points, setPoints] = useState(b.points);
    const dirty = label !== b.label || Number(points) !== b.points;
    return (
        <div
            className="bb-card p-3 grid grid-cols-12 gap-3 items-center"
            data-testid={`behavior-row-${b.behavior_id}`}
        >
            <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="col-span-7 bg-[#080b14] border-[#1f2942]"
            />
            <Input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="col-span-2 bg-[#080b14] border-[#1f2942]"
            />
            <div className="col-span-3 flex gap-2 justify-end">
                <button
                    disabled={!dirty}
                    onClick={() => onSave(b, label, points)}
                    className="bb-btn-ghost !px-3 disabled:opacity-30"
                    data-testid={`save-behavior-${b.behavior_id}`}
                >
                    <Save size={14} />
                </button>
                <button
                    onClick={() => onDelete(b)}
                    className="bb-btn-ghost !px-3 hover:!text-red-400"
                    data-testid={`delete-behavior-${b.behavior_id}`}
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
};

export default Settings;
